/**
 * MedicationEditModal.tsx
 *
 * Add or edit one of the patient's medications (the Medications tab).
 *  - `medication` null → add a new one.
 *  - otherwise → edit it. Remove (for one entered in error) sits in the
 *    footer, behind a confirmation.
 *
 * An e-prescribed medication keeps the drug it was sent with: its name,
 * strength, directions, start date and prescriber are shown read-only, and
 * the API refuses to change them too. Staff can still stop it, remove it and
 * edit its notes.
 *
 * The name suggests as you type (MedicationNameField). Picking a medication
 * offers its strengths; picking one of those also records the drug's RxNorm
 * code. Typing over the name or strength drops the code, since the typed
 * text may no longer be that drug.
 *
 * The prescriber is chosen from the account's doctors and nurses, or typed in
 * for someone not on that list (PrescriberField).
 *
 * Field errors come back from the API (422) and are shown next to each field.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, ConfirmDialog, DatePicker, Input, Modal, Select, Textarea } from '@/components/ui';
import { NLM_ATTRIBUTION, type MedicationKind, type MedicationSuggestion } from '@/lib/medicationSuggestions';
import { extractFieldErrors, getErrorMessage } from '@/services/apiClient';
import { MedicationNameField } from './MedicationNameField';
import { PrescriberField } from './PrescriberField';
import {
  createPatientMedication,
  patientMedicationsKey,
  removePatientMedication,
  updatePatientMedication,
  type PatientMedication,
  type PatientMedicationInput,
} from '@/services/patient-medications.service';

interface MedicationEditModalProps {
  patientId: number;
  medication: PatientMedication | null;
  onClose: () => void;
}

const STATUS_OPTIONS: { label: string; value: PatientMedicationInput['status'] }[] = [
  { label: 'Active', value: 'active' },
  { label: 'Stopped', value: 'stopped' },
];

const KIND_OPTIONS: { label: string; value: MedicationKind }[] = [
  { label: 'Medication', value: 'medication' },
  { label: 'Supplement', value: 'supplement' },
  { label: 'Peptide', value: 'peptide' },
];

function initialForm(medication: PatientMedication | null): PatientMedicationInput {
  return {
    medicationName: medication?.medicationName ?? '',
    strength: medication?.strength ?? '',
    directions: medication?.directions ?? '',
    startDate: medication?.startDate ?? '',
    endDate: medication?.endDate ?? '',
    status: medication?.status ?? 'active',
    prescriberName: medication?.prescriberName ?? '',
    notes: medication?.notes ?? '',
    kind: medication?.kind ?? 'medication',
    rxnorm: medication?.rxnorm ?? '',
  };
}

export function MedicationEditModal({ patientId, medication, onClose }: MedicationEditModalProps) {
  const queryClient = useQueryClient();
  const isEdit = medication !== null;
  const locked = medication?.source === 'eprescribed';

  const [form, setForm] = useState<PatientMedicationInput>(() => initialForm(medication));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  // The suggestion last picked for the name, whose strengths are offered.
  const [picked, setPicked] = useState<MedicationSuggestion | null>(null);

  function set<K extends keyof PatientMedicationInput>(key: K, value: PatientMedicationInput[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function typeName(value: string) {
    setForm((prev) => ({ ...prev, medicationName: value, rxnorm: '' }));
    setPicked(null);
  }

  function pickName(suggestion: MedicationSuggestion) {
    // A strength already in the field that this drug offers keeps its code.
    setForm((prev) => ({
      ...prev,
      medicationName: suggestion.label,
      rxnorm: suggestion.strengths.find((s) => s.text === prev.strength)?.rxcui ?? '',
    }));
    setPicked(suggestion.strengths.length > 0 ? suggestion : null);
  }

  function changeKind(kind: MedicationKind) {
    setForm((prev) => ({ ...prev, kind, rxnorm: '' }));
    setPicked(null);
  }

  function done() {
    void queryClient.invalidateQueries({ queryKey: patientMedicationsKey(patientId) });
    onClose();
  }

  const saveMut = useMutation({
    mutationFn: () =>
      medication
        ? updatePatientMedication(
            patientId,
            medication.id,
            // An e-prescribed row sends only what staff may change. Its drug
            // fields can be refreshed by the prescription while this form is
            // open, and resending the stale copy would be refused.
            locked ? { status: form.status, endDate: form.endDate, notes: form.notes } : form,
          )
        : createPatientMedication(patientId, form),
    onSuccess: done,
    onError: (e: unknown) => {
      setFieldErrors(extractFieldErrors(e));
      setFormError(getErrorMessage(e, "Couldn't save the medication. Try again, and contact support if it keeps happening."));
    },
  });

  const removeMut = useMutation({
    mutationFn: () => removePatientMedication(patientId, (medication as PatientMedication).id),
    onSuccess: done,
    onError: (e: unknown) => {
      setConfirmRemove(false);
      setFormError(getErrorMessage(e, "Couldn't remove the medication. Try again, and contact support if it keeps happening."));
    },
  });

  function handleSave() {
    setFieldErrors({});
    setFormError(null);
    if (form.medicationName.trim() === '') {
      setFieldErrors({ medicationName: 'Enter the name.' });
      return;
    }
    saveMut.mutate();
  }

  const busy = saveMut.isPending || removeMut.isPending;

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        title={isEdit ? 'Edit medication' : 'Add medication'}
        size="lg"
        closeOnEscape={false}
        footer={
          <>
            {isEdit && (
              <Button variant="danger" onClick={() => setConfirmRemove(true)} disabled={busy} className="mr-auto">
                Remove
              </Button>
            )}
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saveMut.isPending} disabled={removeMut.isPending}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {locked && (
            <p className="text-sm text-slate-500">
              Sent as an e-prescription, so the drug details can't be changed. To change them, stop this one and
              add a new one.
            </p>
          )}

          <Select
            label="Type"
            options={KIND_OPTIONS}
            value={form.kind}
            onChange={(v) => changeKind(v ?? 'medication')}
            error={fieldErrors.kind}
            disabled={locked}
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <MedicationNameField
              kind={form.kind}
              value={form.medicationName}
              onChange={typeName}
              onPick={pickName}
              error={fieldErrors.medicationName}
              disabled={locked}
            />
            <div>
              <Input
                label="Strength"
                placeholder="e.g. 10 mg tablet"
                value={form.strength}
                onChange={(e) => setForm((prev) => ({ ...prev, strength: e.target.value, rxnorm: '' }))}
                error={fieldErrors.strength ?? fieldErrors.rxnorm}
                disabled={locked}
                maxLength={100}
              />
              {picked && !locked && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {picked.strengths.map((s) => (
                    <button
                      key={s.text}
                      type="button"
                      onClick={() => setForm((prev) => ({ ...prev, strength: s.text, rxnorm: s.rxcui }))}
                      aria-pressed={form.rxnorm !== '' && form.rxnorm === s.rxcui}
                      className={`rounded-full border px-2.5 py-1 text-xs ${
                        form.rxnorm !== '' && form.rxnorm === s.rxcui
                          ? 'border-slate-700 bg-slate-700 text-white'
                          : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {s.text}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <Textarea
            label="Directions"
            rows={2}
            placeholder="e.g. Take 1 tablet by mouth daily"
            value={form.directions}
            onChange={(e) => set('directions', e.target.value)}
            error={fieldErrors.directions}
            disabled={locked}
            maxLength={2000}
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <DatePicker
              label="Start date"
              value={form.startDate}
              onChange={(v) => set('startDate', v)}
              error={fieldErrors.startDate}
              disabled={locked}
              clearable
            />
            <DatePicker
              label="Stop date"
              value={form.endDate}
              onChange={(v) => set('endDate', v)}
              error={fieldErrors.endDate}
              hint={form.status === 'stopped' && form.endDate === '' ? 'Leave blank to use today.' : undefined}
              clearable
            />
            <Select
              label="Status"
              options={STATUS_OPTIONS}
              value={form.status}
              onChange={(v) => set('status', v ?? 'active')}
              error={fieldErrors.status}
            />
            <PrescriberField
              value={form.prescriberName}
              onChange={(v) => set('prescriberName', v)}
              error={fieldErrors.prescriberName}
              disabled={locked}
            />
          </div>

          <Textarea
            label="Notes"
            rows={3}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            error={fieldErrors.notes}
            maxLength={5000}
          />

          {formError && (
            <p role="alert" className="text-sm text-red-600">
              {formError}
            </p>
          )}

          {/* Required by NLM's terms for the name suggestions. */}
          {!locked && <p className="text-[11px] leading-4 text-slate-400">{NLM_ATTRIBUTION}</p>}
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={() => removeMut.mutate()}
        title="Remove medication?"
        message="Only remove a medication that was entered by mistake. To record that the patient no longer takes it, set its status to Stopped instead."
        confirmLabel="Remove"
        variant="danger"
      />
    </>
  );
}
